import { MenuItemOption } from 'ui/menu/types';
import { pxToRem } from 'utils/convert';
import { ClientDataType } from 'modules/queryBuilder/types';
import {
  Goal,
  GoalAttributionMode,
  GoalEventType,
  GoalStatus,
  GoalsTabFilter,
  GoalsTableColumn,
} from './types';

export const GOALS_PAGE_PREVIEW_KEY = 'goals';

/**
 * Default number of skeleton rows to show when goals are being loaded
 */
export const DEFAULT_NUMBER_OF_GOALS_SKELETON_ROWS = 6;

/**
 * Goals search input debounce timeout in milliseconds
 */
export const SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS = 500;

/**
 * These represent back-end GraphQL enums for sorting
 */
export const SORT_BY_FIELD = {
  NAME: 'Name',
  UPDATED_AT: 'UpdatedAt',
};

export const FILTER_PREFIXES = {
  CREATOR: 'creator-',
  MODIFIER: 'modifier-',
  STATE: 'state-',
};

export const GOALS_INDEX_COLUMNS: GoalsTableColumn[] = [
  {
    label: 'Goal name',
    id: SORT_BY_FIELD.NAME,
  },
  {
    label: 'Last modified',
    id: SORT_BY_FIELD.UPDATED_AT,
  },
];

export enum GoalsFilter {
  LABEL = 'label',
  CREATOR = 'creator',
  MODIFIER = 'modifier',
  MESSAGE_TYPE = 'message-type',
}

export const BASE_GOALS_FILTER_OPTIONS: MenuItemOption[] = [
  {
    id: GoalsFilter.LABEL,
    label: 'Label',
    hasFilter: true,
    isSelectMenu: true,
    options: [
      { id: 'label-1', label: 'Label 1' },
      { id: 'label-2', label: 'Label 2' },
    ],
  },
  {
    id: GoalsFilter.CREATOR,
    hasFilter: true,
    isSelectMenu: true,
    label: 'Creator',
    options: [], // these are added dynamically
  },
  {
    id: GoalsFilter.MODIFIER,
    hasFilter: true,
    isSelectMenu: true,
    label: 'Modified by',
    options: [], // these are added dynamically
  },
];

export const GOALS_HEADER_GRIDS = `
  minmax(${pxToRem(200)}, 1fr) // name
  ${pxToRem(200)} // metadata
`;

export const GOALS_GRIDS = `
  minmax(${pxToRem(200)}, 1fr) // name
  ${pxToRem(200)} // metadata
`;

export const SORT_DIRECTIONS = {
  ASCENDING: 'Ascending',
  DESCENDING: 'Descending',
};

export enum QUERY_PARAMS {
  CREATORS = 'creators',
  MODIFIERS = 'modifiers',
  SEARCH = 'search',
  STATES = 'states', // TODO: update this once product decides on the menu filters
  STATUS = 'status', // TODO: update this once product decides on the tab filters
}

export const GOALS_TAB_FILTERS: GoalsTabFilter[] = [
  {
    id: GoalStatus.ALL,
    label: 'All',
    sortOrder: 0,
    filters: [],
    query: [],
  },
  {
    id: GoalStatus.DRAFT,
    label: 'Draft',
    sortOrder: 1,
    filters: [GoalStatus.DRAFT],
  },
  {
    id: GoalStatus.ACTIVE,
    label: 'Active',
    sortOrder: 2,
    filters: [GoalStatus.ACTIVE],
  },
  {
    id: GoalStatus.ARCHIVED,
    label: 'Archived',
    sortOrder: 3,
    filters: [GoalStatus.ARCHIVED],
  },
];

export const MOCK_GOALS: Goal[] = [
  {
    id: '550e8400-e29b-41d4-a716-446655440000',
    name: 'Increase Monthly Active Users',
    createdAt: '2023-10-01T12:00:00Z',
    createdBy: 'jehf.denezaire@iterable.com',
    updatedBy: 'jehf.denezaire@iterable.com',
    updatedAt: '2023-10-01T12:00:00Z',
    eventName: 'eventName',
    eventType: GoalEventType.CustomEvent,
    attributionMode: GoalAttributionMode.NotSet,
    attributionWindowHours: null,
    isArchived: false,
    messageTypeIds: [],
    projectId: 1,
    campaignLabelIds: [],
    metricDefinition: {
      metric: 'testMetric',
      enumName: 'TotalGoalConversions',
      associatedMetric: 'testAssociatedMetric',
      metricLabel: 'Test Metric',
      associatedMetricLabel: 'Test Associated Metric',
      countLabel: 'Count',
    },
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440001',
    name: 'North America Expansion',
    createdAt: '2023-10-01T12:00:00Z',
    createdBy: 'jehf.denezaire@iterable.com',
    updatedBy: 'jehf.denezaire@iterable.com',
    updatedAt: '2023-10-01T12:00:00Z',
    eventName: ClientDataType.Click,
    eventType: GoalEventType.System,
    attributionMode: GoalAttributionMode.NotSet,
    attributionWindowHours: null,
    isArchived: false,
    messageTypeIds: [],
    projectId: 1,
    campaignLabelIds: [30170, 18454],
    metricDefinition: {
      metric: 'testMetric',
      enumName: 'TotalGoalConversions',
      associatedMetric: 'testAssociatedMetric',
      metricLabel: 'Test Metric',
      associatedMetricLabel: 'Test Associated Metric',
      countLabel: 'Count',
    },
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440002',
    name: 'Black Friday 2025',
    createdAt: '2023-10-01T12:00:00Z',
    createdBy: 'jehf.denezaire@iterable.com',
    updatedBy: 'jehf.denezaire@iterable.com',
    updatedAt: '2023-10-01T12:00:00Z',
    eventName: ClientDataType.Purchase,
    eventType: GoalEventType.Purchase,
    attributionMode: GoalAttributionMode.NotSet,
    attributionWindowHours: null,
    isArchived: false,
    messageTypeIds: [],
    projectId: 1,
    campaignLabelIds: [],
    metricDefinition: {
      metric: 'testMetric',
      enumName: 'TotalGoalConversions',
      associatedMetric: 'testAssociatedMetric',
      metricLabel: 'Test Metric',
      associatedMetricLabel: 'Test Associated Metric',
      countLabel: 'Count',
    },
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440003',
    name: 'Brand Awareness 2025',
    createdAt: '2023-10-01T12:00:00Z',
    createdBy: 'jehf.denezaire@iterable.com',
    updatedBy: 'jehf.denezaire@iterable.com',
    updatedAt: '2023-10-01T12:00:00Z',
    eventName: ClientDataType.PushOpen,
    eventType: GoalEventType.System,
    attributionMode: GoalAttributionMode.NotSet,
    attributionWindowHours: null,
    isArchived: false,
    messageTypeIds: [],
    projectId: 1,
    campaignLabelIds: [],
    metricDefinition: {
      metric: 'testMetric',
      enumName: 'TotalGoalConversions',
      associatedMetric: 'testAssociatedMetric',
      metricLabel: 'Test Metric',
      associatedMetricLabel: 'Test Associated Metric',
      countLabel: 'Count',
    },
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440004',
    name: 'Decrease Online Shopping Cart Abandonment Rate',
    createdAt: '2023-10-01T12:00:00Z',
    createdBy: 'jehf.denezaire@iterable.com',
    updatedBy: 'jehf.denezaire@iterable.com',
    updatedAt: '2023-10-01T12:00:00Z',
    eventName: 'otherEvent',
    eventType: GoalEventType.CustomEvent,
    attributionMode: GoalAttributionMode.NotSet,
    attributionWindowHours: null,
    isArchived: false,
    messageTypeIds: [],
    projectId: 1,
    campaignLabelIds: [34633, 30171, 30172, 53352],
    metricDefinition: {
      metric: 'testMetric',
      enumName: 'TotalGoalConversions',
      associatedMetric: 'testAssociatedMetric',
      metricLabel: 'Test Metric',
      associatedMetricLabel: 'Test Associated Metric',
      countLabel: 'Count',
    },
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440005',
    name: 'Newsletter Signups 2025',
    createdAt: '2023-10-01T12:00:00Z',
    createdBy: 'jehf.denezaire@iterable.com',
    updatedBy: 'jehf.denezaire@iterable.com',
    updatedAt: '2023-10-01T12:00:00Z',
    eventName: ClientDataType.HostedUnsubscribeClick,
    eventType: GoalEventType.System,
    attributionMode: GoalAttributionMode.NotSet,
    attributionWindowHours: null,
    isArchived: false,
    messageTypeIds: [],
    projectId: 1,
    campaignLabelIds: [],
    metricDefinition: {
      metric: 'testMetric',
      enumName: 'TotalGoalConversions',
      associatedMetric: 'testAssociatedMetric',
      metricLabel: 'Test Metric',
      associatedMetricLabel: 'Test Associated Metric',
      countLabel: 'Count',
    },
  },
];
